import { EXPORT_FORMAT_VERSION, exportFiles } from "@szerviz/core";
import { strToU8, zipSync, type Zippable } from "fflate";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "../context";
import type { DataStore } from "../data";
import { ErrorText } from "./ErrorText";
import { Help } from "./Help";

/** Builds the ZIP of the workshop's whole data: tables as CSV and JSON, and the photos. */
export async function buildExportZip(store: DataStore, workshopId: string, workshopName: string, onProgress?: (done: number, total: number) => void): Promise<Uint8Array> {
  const bundle = await store.exportAll(workshopId);
  const files: Zippable = {};
  for (const f of exportFiles(bundle.tables, { workshop: workshopName, exportedAt: new Date().toISOString(), version: EXPORT_FORMAT_VERSION })) {
    files[f.name] = strToU8(f.text);
  }
  let done = 0;
  for (const p of bundle.photos) {
    onProgress?.(done, bundle.photos.length);
    // already compressed images: stored, not deflated again
    files[p.name] = [new Uint8Array(await (await p.load()).arrayBuffer()), { level: 0 }];
    done += 1;
  }
  return zipSync(files);
}

export const exportFileName = (workshopName: string, date = new Date()) =>
  `garagevo_${workshopName.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "").toLowerCase() || "export"}_${date.toISOString().slice(0, 10)}.zip`;

/** Owner only: downloads everything (EU Data Act switching, GDPR portability). */
export function DataExport() {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    setProgress(t("export.preparing"));
    try {
      const zip = await buildExportZip(store, workshop.id, workshop.name, (done, total) => setProgress(t("export.photos", { done, total })));
      const url = URL.createObjectURL(new Blob([zip as BlobPart], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFileName(workshop.name);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setProgress(t("export.done"));
    } catch (err) {
      setError(err);
      setProgress(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2>
        {t("export.title")} <Help id="export.page" />
      </h2>
      <p className="muted">{t("export.intro")}</p>
      <div className="export-box">
        <button type="button" className="primary" disabled={busy} onClick={() => void run()}>
          {t("export.button")}
        </button>
        {progress && <span role="status">{progress}</span>}
      </div>
      <ErrorText error={error} />
    </>
  );
}
